import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { getRequestWithToken, putRequestWithToken } from "../../utils/ApiRequests";

const apiBase = "../v3/admin/candidate-role-mappings";

const labelFor = (value, ...keys) => keys.map((key) => value?.[key]).find(Boolean) || "-";

const CandidateRoleSelectionPanel = ({ candidateId, disabled = false, onSaved, onSelectionChange, showSave = true }) => {
  const [families, setFamilies] = useState([]);
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [familyId, setFamilyId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [mappingId, setMappingId] = useState("");
  const [currentSelection, setCurrentSelection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const loadAllMappings = async (params = {}) => {
    const mappings = [];
    let page = 1;
    let totalPages = 1;
    do {
      const query = new URLSearchParams({ ...params, page, limit: 100 }).toString();
      const res = await getRequestWithToken(`${apiBase}?${query}`);
      mappings.push(...(res.data?.data?.mappings || []));
      totalPages = res.data?.data?.pagination?.totalPages || 1;
      page += 1;
    } while (page <= totalPages);
    return mappings;
  };

  const loadFamilies = async () => {
    const mappings = await loadAllMappings();
    const map = {};
    mappings.forEach((mapping) => {
      const family = mapping.family;
      if (family?.family_id) map[family.family_id] = family;
    });
    setFamilies(Object.values(map).sort((a, b) => labelFor(a, "family_ui_label", "family_name", "family_id").localeCompare(labelFor(b, "family_ui_label", "family_name", "family_id"))));
  };

  const loadGroups = async (nextFamilyId) => {
    setGroups([]);
    setRoles([]);
    if (!nextFamilyId) return;
    const mappings = await loadAllMappings({ family_id: nextFamilyId });
    const map = {};
    mappings.forEach((mapping) => {
      const group = mapping.role_group;
      if (group?.role_group_id) map[group.role_group_id] = group;
    });
    setGroups(Object.values(map).sort((a, b) => labelFor(a, "role_group_ui_label", "role_group_name", "role_group_id").localeCompare(labelFor(b, "role_group_ui_label", "role_group_name", "role_group_id"))));
  };

  const loadRoles = async (nextGroupId) => {
    setRoles([]);
    if (!nextGroupId) return;
    const mappings = await loadAllMappings({ role_group_id: nextGroupId });
    setRoles(mappings.sort((a, b) => labelFor(a.display, "display_title", "candidate_display_title").localeCompare(labelFor(b.display, "display_title", "candidate_display_title"))));
  };

  const loadSelection = async () => {
    if (!candidateId) return;
    const res = await getRequestWithToken(`admin/candidates/${candidateId}/role-selection`);
    const selection = res.data?.data?.selection || null;
    setCurrentSelection(selection);
    const mapping = selection?.mapping_id;
    if (mapping) {
      setFamilyId(mapping.family?.family_id || "");
      setGroupId(mapping.role_group?.role_group_id || "");
      setMappingId(mapping._id || "");
    }
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([loadFamilies(), loadSelection()])
      .catch((err) => mounted && setMessage({ type: "error", text: err?.response?.data?.msg || err.message }))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [candidateId]);

  useEffect(() => { loadGroups(familyId).catch(() => setGroups([])); }, [familyId]);
  useEffect(() => { loadRoles(groupId).catch(() => setRoles([])); }, [groupId]);

  const currentMapping = currentSelection?.mapping_id;
  const selectedRole = useMemo(() => roles.find((role) => role._id === mappingId), [roles, mappingId]);
  const initialMappingId = currentMapping?._id || currentMapping || "";
  const isChanged = Boolean(mappingId) && String(mappingId) !== String(initialMappingId || "");

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange({
        mappingId,
        familyId,
        groupId,
        roleId: selectedRole?.role?.role_id || "",
        initialMappingId: initialMappingId || "",
        isChanged,
      });
    }
  }, [mappingId, familyId, groupId, selectedRole, initialMappingId, isChanged]);

  const saveSelection = async () => {
    if (!candidateId || !mappingId) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await putRequestWithToken(`admin/candidates/${candidateId}/role-selection`, {
        mappingId,
        familyId,
        groupId,
        roleId: selectedRole?.role?.role_id || "",
      });
      const nextSelection = res.data?.data?.selection || null;
      setCurrentSelection(nextSelection);
      setMessage({ type: "success", text: "Role selection saved" });
      if (onSaved) onSaved(nextSelection);
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.msg || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, bgcolor: "background.paper" }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Role Selection</Typography>
          {currentMapping && (
            <Typography variant="body2" color="text.secondary">
              Active: {labelFor(currentMapping.family, "family_ui_label", "family_name")} / {labelFor(currentMapping.role_group, "role_group_ui_label", "role_group_name")} / {labelFor(currentMapping.display, "display_title", "candidate_display_title") || labelFor(currentMapping.role, "role_ui_label", "role_name")}
            </Typography>
          )}
        </Box>
        {message && <Alert severity={message.type}>{message.text}</Alert>}
        {loading ? <CircularProgress size={24} /> : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" disabled={disabled}>
                <InputLabel>Family</InputLabel>
                <Select label="Family" value={familyId} onChange={(event) => { setFamilyId(event.target.value); setGroupId(""); setMappingId(""); }}>
                  <MenuItem value=""><em>Select family</em></MenuItem>
                  {families.map((family) => <MenuItem key={family.family_id} value={family.family_id}>{labelFor(family, "family_ui_label", "family_name", "family_id")}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" disabled={disabled || !familyId}>
                <InputLabel>Group</InputLabel>
                <Select label="Group" value={groupId} onChange={(event) => { setGroupId(event.target.value); setMappingId(""); }}>
                  <MenuItem value=""><em>Select group</em></MenuItem>
                  {groups.map((group) => <MenuItem key={group.role_group_id} value={group.role_group_id}>{labelFor(group, "role_group_ui_label", "role_group_name", "role_group_id")}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" disabled={disabled || !groupId}>
                <InputLabel>Role</InputLabel>
                <Select label="Role" value={mappingId} onChange={(event) => setMappingId(event.target.value)}>
                  <MenuItem value=""><em>Select role</em></MenuItem>
                  {roles.map((role) => <MenuItem key={role._id} value={role._id}>{labelFor(role.display, "display_title", "candidate_display_title") || labelFor(role.role, "role_ui_label", "role_name", "role_id")}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
        {showSave && (
          <Stack direction="row" justifyContent="flex-end">
            <Button variant="contained" onClick={saveSelection} disabled={disabled || !candidateId || !mappingId || saving}>
              {saving ? "Saving..." : selectedRole ? "Save Role" : "Save"}
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default CandidateRoleSelectionPanel;
